import Tags from 'components/Tags/Tags';
import styles from './productSelectField.module.scss';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductListDataContext } from 'containers/RetailRMN/Products/ProductListDataContext';
import { ProductHome } from 'containers/RetailRMN/Products/ProductHome';
import { compact } from 'lodash';
import { Product } from 'core/product/Product';
import { ProductFilter } from 'containers/RetailRMN/Products/ProductFilter';
import { Modal } from 'components/Modal/Modal';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { DefaultDmpManager, DmpManager } from 'core/dmp/DmpManager';
import { withFormikField } from 'hoc/withFormikField';
import Tips from 'components/Tips/Tips';
import classnames from 'classnames/bind';
import { SelectOptions } from 'components/commonType';
import { CopyableOverlayTrigger } from 'components/CopyableOverlayTrigger/CopyableOverlayTrigger';

const cx = classnames.bind(styles);

const dmpManager: DmpManager = new DefaultDmpManager();
export const ProductSelectField: React.FC<{
  advertiserId?: number;
  value: string[];
  error?: string;
  onChange: (newValue: string[]) => void;
}> = ({ advertiserId, value, error, onChange }) => {

  const {
    loading,
    callAPIs
  } = useCallAPI();

  const productCache = useRef<{[key: string]: Product}>({});
  const selectedProducts = useRef<Product[]>(compact(value.map(productId => productCache.current[productId] || undefined)));
  const [initedProductCache, setInitedProductCache] = useState(false);

  useEffect(() => {
    if (!advertiserId) {
      return;
    }

    if (initedProductCache) {
      return;
    }

    const productIds = value.map(productId => {
      return productId;
    });
    const needFetchCacheProducts = productIds.filter(id => !(id in productCache.current));
    if (needFetchCacheProducts.length === 0) {
      setInitedProductCache(true);
      return;
    }
    callAPIs(
      [() => dmpManager.getProductCaches(advertiserId, productIds)], (productCaches: Product[]) => {
        productCaches.forEach(product => {
          productCache.current[product.productId] = product;
        });
        selectedProducts.current = compact(value.map(productId => productCache.current[productId]));
        setInitedProductCache(true);
      }
    );
  }, [advertiserId, value, initedProductCache, callAPIs]);

  const defaultFilters = useMemo(
    () => ({
      advertiser: advertiserId
    }),
    [advertiserId]
  );
  const [productListModal, setProductListModal] = useState<any>();
  const [filterSet, setFilterSet] = useState<ProductFilter>();

  const handleSelectedProducts = useCallback((fn: (selectedProducts: Product[]) => Product[]): void => {
    selectedProducts.current = fn(selectedProducts.current);
    selectedProducts.current.forEach(product => {
      productCache.current[product.productId] = product;
    });
  }, []);

  const handleFilterChanged = useCallback((filterSet: ProductFilter): void => {
    setFilterSet(filterSet);
  }, []);

  const onModalConfirm = useCallback(() => {
    onChange(selectedProducts.current.map(product => product.productId));
    setProductListModal(undefined);
  }, [onChange]);

  const onModalCancel = useCallback(() => {
    selectedProducts.current = compact(value.map(productId => {
      return productCache.current[productId];
    }));
    setProductListModal(undefined);
  }, [value]);

  const productListModalData = useMemo(
    () => ({
      render: () => (
        <ProductListDataContext.Provider
          value={{
            selectedProducts: selectedProducts.current,
            handleSelectedProducts,
            handleFilterChanged
          }}
        >
          <ProductHome defaultFilters={filterSet || defaultFilters} />
        </ProductListDataContext.Provider>
      ),
      props: {
        title: '',
        primaryButton: {
          title: i18n.t<string>('common.buttons.confirm'),
          callback: onModalConfirm
        },
        secondaryButton: {
          title: i18n.t<string>('common.buttons.cancel'),
          callback: onModalCancel
        },
        dismiss: onModalCancel,
        className: styles.productListModal
      }
    }),
    [
      filterSet,
      defaultFilters,
      handleSelectedProducts,
      handleFilterChanged,
      onModalConfirm,
      onModalCancel
    ]
  );

  const onModalOpen = useCallback(
    () => setProductListModal(productListModalData),
    [productListModalData]
  );

  const renderModal = useCallback(() => {
    if (!productListModal) {
      return null;
    }
    const render = productListModal.render;
    const props = productListModal.props;
    return (
      <Modal
        fullScreen={true}
        title={props.title}
        primaryButton={props.primaryButton}
        secondaryButton={props.secondaryButton}
        dismiss={props.dismiss}
        className={props.className}
        animation={false}
      >
        {render ? render() : <div />}
      </Modal>
    );
  }, [productListModal]);

  const tagsClassName = cx(styles.tags, {
    hasError: error !== undefined
  });

  const tagsValue = useMemo(() => {
    if (!initedProductCache) {
      return [];
    }

    return value.map(productId => {
      const product = productCache.current[productId];
      if (!product) {
        return {
          label: productId,
          value: productId
        };
      }
      return {
        label: product.name,
        value: product.productId.toString()
      };
    });
  }, [value, initedProductCache]);

  const onTagsChange = useCallback((newTags: SelectOptions[]) => {
    selectedProducts.current = newTags.map(option => {
      return productCache.current[option.value.toString()];
    });
    onChange(selectedProducts.current.map(product => product.productId));
  }
  , [onChange]);

  return (
    <>
      {loading && <LoadingIndicator />}
      {renderModal()}
      {value.length > 0 ? (
        <div className={styles.tagsContainer}>
          <div className={styles.tagsInput}>
            <Tags
              value={tagsValue}
              disableInput
              onChange={onTagsChange}
              className={tagsClassName}
            />
            <Tips>{error}</Tips>
          </div>
          <div className={styles.editIcon}>
            <IconWithTooltip
              icon={faPencilAlt}
              tooltipProps={{
                id: `productNativeFormEditProducts`,
                tooltip: i18n.t<string>(
                  'creativeSetupFlow.labels.editProductsHint'
                )
              }}
              onClick={onModalOpen}
            />
          </div>
        </div>
      ) : (
        <CopyableOverlayTrigger
          placement='top'
          trigger='hover'
          overlay={
            !advertiserId ? (
              <span className={styles.selectProductsHint}>
                {i18n.t<string>('productSelectField.hints.needAdvertiser')}
              </span>
            ) : undefined
          }
        >
          <div className={styles.selectProductsRow}>
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              disabled={!advertiserId}
              onClick={onModalOpen}
            >
              {i18n.t<string>('creativeSetupFlow.labels.selectProducts')}
            </button>
            <Tips>{error}</Tips>
          </div>
        </CopyableOverlayTrigger>
      )}
    </>
  );
};

export type FormikProductSelectFieldProps = {
  name: string;
  advertiserId?: number;
  validate?: (categories: string[]) => string | undefined;
};

export const FormikProductSelectField = withFormikField<FormikProductSelectFieldProps>(ProductSelectField);

const MAX_PRODUCTS = 400;

const validateProducts = (productIds: string[]) => {
  if (productIds.length === 0) {
    return i18n.t<string>('productNativeCreativeForm.errors.cannotBeEmpty');
  }
  if (productIds.length > MAX_PRODUCTS) {
    return i18n.t<string>('productSelectField.errors.exceedLimit', { max: MAX_PRODUCTS });
  }
  return undefined;
};

export const renderFormikProductSelectField = (
  name: string,
  advertiserId?: number,
  validate: (
    productIds: string[]
  ) => string | undefined = validateProducts
) => {
  return (
    <FormikProductSelectField
      name={name}
      advertiserId={advertiserId}
      validate={validate}
    />
  );
};
